import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FieldWrapper, InputField, SelectFieldCountry } from "@/components/common";
import { useAuth } from "@/contexts/auth-context";
import { editUser } from "@/services/auth-service";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import IconHelp from "@/assets/icons/icon-help.svg";

const COUNTRY_OPTIONS = ["한국", "미국", "일본", "유럽"] as const;

const profileSchema = z.object({
  ceo_name: z.string().min(1, "필수 입력 항목입니다").max(100, "최대 100자까지 입력 가능합니다"),
  company_country: z
    .string()
    .min(1, "필수 입력 항목입니다")
    .max(50, "최대 50자까지 입력 가능합니다"),
  phone: z.string().min(1, "필수 입력 항목입니다"),
  department: z.string().min(1, "필수 입력 항목입니다").max(50, "최대 50자까지 입력 가능합니다"),
  position: z.string().min(1, "필수 입력 항목입니다").max(50, "최대 50자까지 입력 가능합니다"),
  job_title: z.string().min(1, "필수 입력 항목입니다").max(50, "최대 50자까지 입력 가능합니다"),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface ProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function ProfileEditDialog({ open, onOpenChange }: ProfileEditDialogProps) {
  const { user, refreshUser, withdraw } = useAuth();
  const { toast } = useToast();
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [vv, setVv] = useState({ height: "100dvh", top: 0 });

  useEffect(() => {
    if (!window.visualViewport) return;
    const update = () => {
      setVv({
        height: `${window.visualViewport!.height}px`,
        top: window.visualViewport!.offsetTop,
      });
    };
    window.visualViewport.addEventListener("resize", update);
    window.visualViewport.addEventListener("scroll", update);
    update();
    return () => {
      window.visualViewport!.removeEventListener("resize", update);
      window.visualViewport!.removeEventListener("scroll", update);
    };
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (open && user) {
      reset({
        ceo_name: user.ceo_name || "",
        company_country: user.company_country || "한국",
        phone: formatPhone((user.phone || "").replace(/-/g, "")),
        department: user.department || "",
        position: user.position || "",
        job_title: user.job_title || "",
      });
      setPhoneDisplay(formatPhone((user.phone || "").replace(/-/g, "")));
    }
  }, [open, user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await editUser({
        ceo_name: data.ceo_name,
        phone: data.phone.replace(/-/g, ""),
        department: data.department,
        position: data.position,
        job_title: data.job_title,
        company_country: data.company_country.trim(),
      });
      await refreshUser();
      toast({ description: "정보가 수정되었습니다." });
      onOpenChange(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error?.code;

        if (errorCode === "#M001") {
          setError("phone", { type: "manual", message: "유효하지 않은 형식입니다." });
        }
        if (errorCode === "#M005") {
          setError("phone", { type: "manual", message: "이미 등록된 이메일입니다." });
        }
        if (errorCode === "#M007") {
          setError("phone", { type: "manual", message: "유효하지 않은 전화번호입니다." });
        }
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    setPhoneDisplay(digits);
    setValue("phone", formatPhone(digits), { shouldValidate: false });
    clearErrors("phone");
  };

  const handlePhoneBlur = () => {
    const formatted = formatPhone(phoneDisplay);
    setPhoneDisplay(formatted);
  };

  const handleWithdrawConfirm = async () => {
    try {
      setWithdrawLoading(true);
      await withdraw();
      toast({ description: "탈퇴가 완료되었습니다." });
      setShowWithdrawDialog(false);
      onOpenChange(false);
    } catch {
      // already handled by withdraw in auth-context
    } finally {
      setWithdrawLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={
            {
              "--vv-height": vv.height,
              "--vv-top": `${vv.top}px`,
              colorScheme: "light",
            } as React.CSSProperties
          }
          className="w-[calc(100vw-32px)] sm:w-auto max-w-[720px] lg:max-w-[650px] overflow-y-auto custom-scrollbar !rounded-[4px] border border-[rgba(0,0,0,0.15)] bg-white p-6 shadow-[0px_8px_16px_rgba(0,0,0,0.09),0px_0px_36px_rgba(0,0,0,0.11)] backdrop-blur-[100px] [&>button_svg]:w-6 [&>button_svg]:h-6 sm:max-h-[90dvh] max-sm:max-h-[calc(var(--vv-height)-2rem)] max-sm:!top-[calc(var(--vv-top)+1rem)] max-sm:!bottom-auto max-sm:!translate-y-0 max-sm:!m-0"
        >
          <DialogTitle className="text-2xl font-bold leading-[28px] lg:leading-8 tracking-[-0.0183em] lg:tracking-[-0.02em] text-[#202020] pr-12">
            내 정보 수정
          </DialogTitle>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 lg:gap-6">
            <div className="flex flex-col gap-3 pb-6 border-b border-[rgba(0,0,0,0.15)]">
              <h4 className="text-xl font-bold leading-[24px] lg:leading-[28px] tracking-[-0.018em] lg:tracking-[-0.02em] text-[#000000]">
                회사 정보
              </h4>

              <FieldWrapper label="법인명" cssLabel="text-[#D8D8D8]">
                <InputField
                  placeholder="법인명을 작성해주세요"
                  value={user?.company_name || ""}
                  disabled
                />
              </FieldWrapper>

              <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
                <FieldWrapper
                  label="대표명"
                  required
                  error={errors.ceo_name?.message}
                  className="flex-1 !text-black"
                >
                  <InputField
                    placeholder="대표명을 작성해주세요"
                    maxLength={100}
                    error={!!errors.ceo_name}
                    {...register("ceo_name", {
                      onChange: () => clearErrors("ceo_name"),
                    })}
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="기업 소재국"
                  required
                  error={errors.company_country?.message}
                  className="flex-1"
                >
                  <SelectFieldCountry
                    options={COUNTRY_OPTIONS}
                    value={watch("company_country")}
                    placeholder="국가 선택"
                    allowCustom
                    customMaxLength={50}
                    onChange={(val: string) => {
                      setValue("company_country", val, { shouldValidate: false });
                      clearErrors("company_country");
                    }}
                  />
                  <input type="hidden" {...register("company_country")} />
                </FieldWrapper>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <h4 className="text-xl font-bold leading-[24px] lg:leading-[28px] tracking-[-0.018em] lg:tracking-[-0.02em] text-[#000000]">
                계정 설정
              </h4>

              <FieldWrapper label="의뢰자명" cssLabel="text-[#D8D8D8]">
                <InputField
                  placeholder="의뢰자명을 작성해주세요"
                  value={user?.requester_name || ""}
                  disabled
                />
              </FieldWrapper>

              <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
                <FieldWrapper label="이메일" className="flex-1" cssLabel="text-[#D8D8D8]">
                  <InputField
                    placeholder="이메일 입력해 주세요"
                    value={user?.email || ""}
                    disabled
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="전화번호"
                  required
                  error={errors.phone?.message}
                  className="flex-1"
                >
                  <InputField
                    placeholder="010-1234-5678"
                    value={phoneDisplay}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    error={!!errors.phone}
                    inputMode="numeric"
                  />
                  <input
                    type="hidden"
                    {...register("phone", {
                      validate: (val) => {
                        if (!val) return "필수 입력 항목입니다";
                        const digits = (val || "").replace(/-/g, "");
                        if (!/^010\d{7,8}$/.test(digits)) return "유효하지 않은 전화번호입니다.";
                        return true;
                      },
                    })}
                  />
                </FieldWrapper>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
                <FieldWrapper
                  label="부서"
                  required
                  error={errors.department?.message}
                  className="flex-1"
                >
                  <InputField
                    placeholder="부서 입력해 주세요"
                    maxLength={50}
                    error={!!errors.department}
                    {...register("department", {
                      onChange: () => clearErrors("department"),
                    })}
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="직급"
                  required
                  error={errors.position?.message}
                  className="flex-1"
                >
                  <InputField
                    placeholder="직급 입력해 주세요"
                    maxLength={50}
                    error={!!errors.position}
                    {...register("position", {
                      onChange: () => clearErrors("position"),
                    })}
                  />
                </FieldWrapper>
                <FieldWrapper
                  label="직책"
                  required
                  error={errors.job_title?.message}
                  className="flex-1"
                >
                  <InputField
                    placeholder="직책 입력해 주세요"
                    maxLength={50}
                    error={!!errors.job_title}
                    {...register("job_title", {
                      onChange: () => clearErrors("job_title"),
                    })}
                  />
                </FieldWrapper>
              </div>
            </div>

            <div className="flex flex-row items-center justify-end gap-3 w-full">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full lg:flex-1 rounded-[4px] lg:rounded-[8px] h-12 bg-[#EBEBEB] text-[#000000] font-bold text-base leading-6 tracking-[-0.01em] backdrop-blur-[20px]"
                variant="ghost"
              >
                취소
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full lg:flex-1 rounded-[4px] lg:rounded-[8px] h-12 text-[#000000] font-bold text-base leading-6 tracking-[-0.01em] bg-[#FFAA72]"
                variant="ghost"
              >
                저장
              </Button>
            </div>
          </form>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setShowWithdrawDialog(true)}
              className="text-base font-bold text-black underline hover:opacity-80 transition-opacity"
            >
              회원탈퇴
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={showWithdrawDialog}
        onOpenChange={setShowWithdrawDialog}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        iconVariant="neutral"
        title="정말 탈퇴하시겠습니까?"
        description="탈퇴 후에는 계정을 복구할 수 없으며, 관련 데이터가 모두 삭제됩니다"
        primaryAction={{
          label: "네",
          onClick: handleWithdrawConfirm,
        }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setShowWithdrawDialog(false),
        }}
      />
    </>
  );
}
